import { useMemo } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  LogEntriesPerDateFetchStrategy,
  LogTemplateType,
  LogFieldValueInput,
  useAllWorklogsTemplatesForKpiTrackerQuery,
  useLogEntriesPerDateQuery,
  useUpsertLogEntryMutation,
  LogCategory,
  LogsTemplate,
  LogEntriesPerDateDocument,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import * as kpiState from '../state';
import { KpiFormValues, WorklogsField } from '../types';
import type { KpiWorkLogFormContextValue } from '../types';
import { convertValueByUnit, formatDateToYYYYMMDD, getSectionUnitForField } from '../utils';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { extractErrorMessageFromUnknownError } from '@/utils/error';
import * as globalState from '@/components/Analysis/state';
import { COLLECTION_NAMES } from '@/constants';

export const useKpiWorkLogForm = () => {
  const structureId = useGetStructureId();
  const selectedWorkpackId = useAtomValue(kpiState.selectedWorkpackIdForKpiTracker);
  const setSelectedWorklogsTemplate = useSetAtom(kpiState.selectedWorklogsTemplate);
  const selectedDate = useAtomValue(kpiState.selectedDate);
  const unitSystem = useAtomValue(globalState.unitSystem);
  const selectedShift = useAtomValue(kpiState.selectedShift);
  const { showSnackBar } = useSnackBarMessage({});
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formattedDate = formatDateToYYYYMMDD(selectedDate);

  const commonWorkpackLogsPerDateQueryInput = useMemo(
    () => ({
      timezone: userTimezone,
      logDate: formattedDate,
      structureId,
      referenceId: selectedWorkpackId || '',
      collectionName: COLLECTION_NAMES.WORKPACK,
      category: LogCategory.Worklog,
    }),
    [selectedWorkpackId, structureId, userTimezone, formattedDate]
  );

  // Fetch worklogs template
  const {
    data: templateData,
    error: templateError,
    loading: templateLoading,
  } = useAllWorklogsTemplatesForKpiTrackerQuery({
    variables: {
      input: {
        structureId,
        type: LogTemplateType.WorklogTemplate,
      },
    },
    fetchPolicy: 'cache-first',
    skip: !structureId,
    onCompleted: (data) => {
      if (data?.allLogsTemplates?.at(0)) {
        setSelectedWorklogsTemplate(data.allLogsTemplates.at(0) as LogsTemplate);
      }
    },
  });

  const { data: todaysLogData } = useLogEntriesPerDateQuery({
    variables: {
      input: {
        ...commonWorkpackLogsPerDateQueryInput,
        shift: selectedShift,
        fetchStrategy: LogEntriesPerDateFetchStrategy.Individual,
      },
    },
    skip: !selectedWorkpackId || !structureId,
    fetchPolicy: 'cache-first',
  });

  // Aggregated logs
  const { data: logsData, loading: logsLoading } = useLogEntriesPerDateQuery({
    variables: {
      input: {
        ...commonWorkpackLogsPerDateQueryInput,
        fetchStrategy: LogEntriesPerDateFetchStrategy.Aggregated,
      },
    },
    skip: !selectedWorkpackId || !structureId,
    fetchPolicy: 'cache-first',
  });

  const [upsertLogEntry, { loading: isSubmitting }] = useUpsertLogEntryMutation();

  const template = templateData?.allLogsTemplates?.at(0);
  const sections = template?.sections;
  const workpackLogs = logsData?.logEntriesPerDate;
  const isQuerySkipped = !structureId;

  // Build initial form values from template fields
  const initialValues = useMemo<KpiFormValues>(() => {
    const values: KpiFormValues = {};

    if (template?.fields) {
      template.fields.forEach((field: WorklogsField) => {
        if (field.key) {
          // check if today's log has data for this field
          const todaysLog = todaysLogData?.logEntriesPerDate?.find(
            (log) => log.fieldKey === field.key
          );
          if (todaysLog) {
            values[field.key] = {
              value: convertValueByUnit(
                todaysLog.total,
                getSectionUnitForField(field.key, sections),
                unitSystem,
                'view'
              ),
            };
          } else {
            values[field.key] = { value: '' };
          }
        }
      });
    }

    return values;
  }, [template?.fields, todaysLogData?.logEntriesPerDate, sections, unitSystem]);

  // Build validation schema dynamically from template fields
  const validationSchema = useMemo(() => {
    if (!template?.fields) return Yup.object({});

    const schemaFields = template.fields.reduce<Record<string, Yup.Schema>>(
      (acc: Record<string, Yup.Schema>, field: WorklogsField) => {
        if (field.key) {
          acc[field.key] = Yup.object({
            value: Yup.mixed()
              .test('is-valid-number', 'Must be a valid number', (value) => {
                if (value === '' || value === undefined || value === null) return true;
                return typeof value === 'number' && !Number.isNaN(value);
              })
              .test('is-non-negative', 'Value must be 0 or greater', (value) => {
                if (value === '' || value === undefined || value === null) return true;
                return typeof value === 'number' && value >= 0;
              }),
          });
        }
        return acc;
      },
      {}
    );

    // Add POB validation
    schemaFields.pob = Yup.object({
      value: Yup.mixed()
        .test('is-valid-number', 'Must be a valid number', (value) => {
          if (value === '' || value === undefined || value === null) return true;
          return typeof value === 'number' && !Number.isNaN(value);
        })
        .test('is-non-negative', 'Value must be 0 or greater', (value) => {
          if (value === '' || value === undefined || value === null) return true;
          return typeof value === 'number' && value >= 0;
        }),
    });

    return Yup.object(schemaFields);
  }, [template?.fields]);

  // Build field values from form values for upsertLogEntry
  const buildFieldValues = (values: KpiFormValues): LogFieldValueInput[] => {
    return Object.entries(values).map(([fieldKey, fieldValue]) => {
      if (fieldValue.value === '') {
        return { fieldKey, value: 0, delete: true };
      }
      const entry: LogFieldValueInput = {
        fieldKey,
        value: convertValueByUnit(
          fieldValue.value,
          getSectionUnitForField(fieldKey, sections),
          unitSystem,
          'update'
        ),
      };
      return entry;
    });
  };

  const formik = useFormik<KpiFormValues>({
    initialValues,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!selectedWorkpackId || !structureId || !template?.id) return;

      const fieldValues = buildFieldValues(values);
      const allDeleted = fieldValues.length > 0 && fieldValues.every((fv) => fv.delete);

      try {
        await upsertLogEntry({
          variables: {
            input: {
              logDate: formattedDate,
              structureId,
              referenceId: selectedWorkpackId,
              collectionName: COLLECTION_NAMES.WORKPACK,
              category: LogCategory.Worklog,
              logsTemplateId: template.id,
              shift: selectedShift,
              fieldValues,
              lastModifiedFor: allDeleted ? 'Delete Work Log' : 'Update Work Log',
            },
          },
          refetchQueries: [LogEntriesPerDateDocument],
        });
      } catch (error) {
        const message = extractErrorMessageFromUnknownError(error);
        showSnackBar(message, 'error');
      }
    },
  });

  const value = useMemo<KpiWorkLogFormContextValue>(
    () => ({
      formik,
      isSubmitting,
      template,
      sections,
      workpackLogs,
      logsLoading,
      isLoading: templateLoading || isQuerySkipped,
      error: templateError,
    }),
    [
      formik,
      isSubmitting,
      template,
      sections,
      workpackLogs,
      logsLoading,
      templateLoading,
      isQuerySkipped,
      templateError,
    ]
  );

  // Store in Jotai atom for child components to access synchronously
  const setFormContext = useSetAtom(kpiState.kpiWorkLogFormContextAtom);
  setFormContext(value);

  return value;
};

/**
 * Hook for child components to access the shared form context.
 * Must be used within a component tree where useKpiWorkLogForm has been called.
 */
export const useKpiWorkLogFormContext = (): KpiWorkLogFormContextValue => {
  const context = useAtomValue(kpiState.kpiWorkLogFormContextAtom);

  if (!context) {
    throw new Error(
      'useKpiWorkLogFormContext must be used after useKpiWorkLogForm has been called'
    );
  }

  return context;
};
